import React, { useCallback, useMemo } from 'react';

import formatRewardRate from 'components/DriverAX/utils/formatRewardRate';
import { isArnoldBrosRewardBands } from 'components/DriverAX/utils/rewardBandsUtils';
import useValueFormatters from 'hooks/useValueFormatters';
import numberToCommaString from 'hooks/useValueFormatters/numberToCommaString';
import Typography from 'kingpin/atoms/Typography';

/**
 * The reward-override dropdown's option list + collapsed label, shared by the
 * reward-line control (RewardOverrideControl) and the per-week fact-table cell
 * (WeeklyRewardOverrideCell). Both present the same choice — pick a payout option
 * (identified by rate/amount) or revert a persisted override to Baseline — so the
 * option building, band description, and selected-label logic live here once.
 *
 * Callers supply the reward's context (options entry, the reward output for band
 * descriptions, whether a persisted override exists, the staged override, and a
 * `stageRewardOverride` sink). Selecting an option stages a Set; choosing the
 * currently-active option with no persisted override clears the staging; Baseline
 * stages a Clear.
 */
const useRewardOverrideDropdown = ({
  rewardName,
  rewardType,
  units,
  dataset,
  reward,
  optionsEntry,
  hasPersistedOverride,
  stagedOverride,
  stageRewardOverride,
  label,
  formatRewardValueOverride,
}: {
  rewardName: string;
  rewardType: BonusTypes.BonusProgram['rewardType'];
  units: string | undefined;
  dataset: string | undefined;
  reward: BonusTypes.Rewards.AnyRewardOutput | undefined;
  optionsEntry: BonusTypes.RewardOverrideOptions | undefined;
  hasPersistedOverride: boolean;
  stagedOverride: BonusTypes.Overrides.UserInputRewardOverride | undefined;
  stageRewardOverride: (
    override: BonusTypes.Overrides.UserInputRewardOverride | undefined,
  ) => void;
  label?: string;
  // Formats a reward value for display, overriding the rewardType-based $/PTS
  // choice. The fact-table cell passes the column field's own formatter so an
  // option reads like its column (e.g. WEL's `safetyBonusPay` monetary twin shows
  // `$25` even though the program is points).
  formatRewardValueOverride?: (value: number) => string;
}) => {
  const { formatValue, formatField } = useValueFormatters();

  // The reward's bands, used to describe each override option by the band it maps
  // to. Skip Arnold-shaped bands (multi-component; no single threshold ladder).
  const rewardBands = useMemo(() => {
    const bands = reward?.rewardBands;
    if (!bands || isArnoldBrosRewardBands(bands)) {
      return undefined;
    }
    return bands;
  }, [reward]);

  // Dave's program-level `units` field tells us when override options are
  // percentages rather than money/rates. Fall back to the reward type for older
  // program documents which don't have it yet.
  const isPercentageReward = useMemo(
    () => units === '%' || reward?.type === 'scaledMultiplierBanded',
    [reward, units],
  );

  const describeOptionBand = useCallback(
    (option: BonusTypes.RewardOverrideOption): string | undefined => {
      if (!rewardBands || dataset === undefined) {
        return undefined;
      }
      const value = option.rate ?? option.amount;
      if (value === undefined) {
        return undefined;
      }
      const band = rewardBands.bands.find((b) => b.value === value);
      if (!band) {
        return undefined;
      }
      const threshold = band.min ?? band.max ?? band.equal;
      if (threshold === undefined) {
        return undefined;
      }
      const operator = band.min != null ? '>' : band.max != null ? '<' : '=';
      const formattedThreshold = formatField({
        field: rewardBands.field,
        dataset,
        value: threshold,
      });
      return `${operator} ${formattedThreshold}`;
    },
    [rewardBands, dataset, formatField],
  );

  const formatReward = useCallback(
    (value: number): string => {
      if (formatRewardValueOverride) {
        return formatRewardValueOverride(value);
      }
      if (rewardType === 'points') {
        return `${numberToCommaString({ value })} PTS`;
      }
      return formatValue({
        value,
        formatting: { prefix: 'currency', precision: 2, currencyType: 'USD' },
      }).toString();
    },
    [formatValue, rewardType, formatRewardValueOverride],
  );

  const formatRate = useCallback(
    (rate: number): string => formatRewardRate({ rate, rewardType, units }),
    [rewardType, units],
  );

  const getOptionPrimaryLabel = useCallback(
    (option: BonusTypes.RewardOverrideOption): string => {
      const isRate = option.rate !== undefined;
      if (isPercentageReward) {
        return `${option.rate ?? option.amount ?? 0}%`;
      }
      return isRate
        ? formatRate(option.rate ?? 0)
        : formatReward(option.amount ?? 0);
    },
    [formatRate, formatReward, isPercentageReward],
  );

  const isOptionMatchedByStagedOverride = useCallback(
    (option: BonusTypes.RewardOverrideOption): boolean => {
      if (stagedOverride?.action !== 'Set') {
        return false;
      }
      if (option.rate !== undefined) {
        return stagedOverride.rate === option.rate;
      }
      return stagedOverride.amount === option.amount;
    },
    [stagedOverride],
  );

  const selectedOption = useMemo(() => {
    if (!optionsEntry || stagedOverride?.action === 'Clear') {
      return undefined;
    }
    const stagedOption = optionsEntry.options.find(
      isOptionMatchedByStagedOverride,
    );
    if (stagedOption) {
      return stagedOption;
    }
    if (stagedOverride) {
      return undefined;
    }
    return optionsEntry.options.find((option) => option.active);
  }, [isOptionMatchedByStagedOverride, optionsEntry, stagedOverride]);

  const options = useMemo((): DropdownOption[] => {
    if (!optionsEntry) {
      return [];
    }

    const baselineOption: DropdownOption | undefined = hasPersistedOverride
      ? {
          key: 'baseline',
          label: `Baseline · ${formatReward(optionsEntry.currentReward)}`,
          isSelected: stagedOverride?.action === 'Clear',
          onSelected: () => {
            // Reverting a persisted override stages a Clear (→ DELETE on confirm).
            stageRewardOverride({ action: 'Clear', rewardName });
          },
        }
      : undefined;

    const overrideOptions: DropdownOption[] = optionsEntry.options.map(
      (option, index) => {
        const isRate = option.rate !== undefined;
        const primaryLabel = getOptionPrimaryLabel(option);
        const isSelected = stagedOverride
          ? isOptionMatchedByStagedOverride(option)
          : !!option.active;

        // Two-line option: line 1 (`label`) describes the band the option maps to
        // (e.g. "> 10,000 miles"), line 2 (`subLabel`) the resulting reward. When
        // the option doesn't map to a band, line 1 is the rate/amount/percentage.
        const bandLabel = describeOptionBand(option);
        const payout = formatReward(option.reward);
        const optionLabel = bandLabel ?? primaryLabel;
        const optionSubLabel = bandLabel
          ? isPercentageReward
            ? `${option.rate ?? option.amount ?? 0}% → ${payout}`
            : isRate
              ? `${formatRate(option.rate ?? 0)} → ${payout}`
              : payout
          : isRate
            ? `→ ${payout}`
            : undefined;
        return {
          key: `option-${index}`,
          // Stable value → stable `select-…` test id, independent of the label.
          value: isRate ? `rate-${option.rate}` : `amount-${option.amount}`,
          label: optionLabel,
          subLabel: optionSubLabel,
          isSelected,
          onSelected: () => {
            if (!hasPersistedOverride && option.active) {
              stageRewardOverride(undefined);
              return;
            }
            stageRewardOverride({
              action: 'Set',
              rewardName,
              rate: option.rate,
              amount: option.amount,
              note: '',
            });
          },
        };
      },
    );

    return baselineOption
      ? [baselineOption, ...overrideOptions]
      : overrideOptions;
  }, [
    optionsEntry,
    hasPersistedOverride,
    stagedOverride,
    formatReward,
    formatRate,
    stageRewardOverride,
    rewardName,
    isPercentageReward,
    describeOptionBand,
    getOptionPrimaryLabel,
    isOptionMatchedByStagedOverride,
  ]);

  const fallbackLabel = useMemo(() => {
    if (stagedOverride?.action === 'Clear') {
      return `Baseline · ${formatReward(optionsEntry?.currentReward ?? 0)}`;
    }
    if (selectedOption) {
      return getOptionPrimaryLabel(selectedOption);
    }
    return label ?? 'Adjust payout';
  }, [
    formatReward,
    getOptionPrimaryLabel,
    label,
    optionsEntry?.currentReward,
    selectedOption,
    stagedOverride,
  ]);

  const renderSelectedLabel = useCallback(
    () => (
      <Typography.Body type={'Label'} isEllipsis>
        {fallbackLabel}
      </Typography.Body>
    ),
    [fallbackLabel],
  );

  return {
    options,
    selectedOption,
    fallbackLabel,
    renderSelectedLabel,
    isPercentageReward,
  };
};

export default useRewardOverrideDropdown;
